class SkillDevelopmentService {
  constructor() {
    // Use backend CMS API for secure data fetching
    this.cmsBaseURL = process.env.REACT_APP_API_URL;
    
    // Fallback data in case API fails (public property)
    this.fallbackData = {
      totalCourses: 0,
      totalRegister: 0,
      totalcomplete: 0,
      trainingNumber: 0,
      hasUpcomingBatches: true,
    };
  }

  // Fetch all courses data through backend CMS
  async getAllCourses() {
    try {
      const response = await fetch(`${this.cmsBaseURL}/external-data/ebs-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: '/request/get_all_courses',
          data: {} // Add empty data object to match API expectation
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      // Return raw data so that the hook can transform it
      if (result.data && result.data.status === 200 && result.data.data) {
        return result.data.data; // Return the nested data object
      } else {
        console.warn('⚠️ SkillDevelopmentService: API data structure unexpected, using fallback data');
        console.warn('Expected: result.data.status=200 and result.data.data, got:', {
          hasResultData: !!result.data,
          status: result.data?.status,
          hasNestedData: !!result.data?.data
        });
        return this.fallbackData;
      }
    } catch (error) {
      console.error('❌ SkillDevelopmentService: Error getting courses data:', error);
      console.warn('🔄 SkillDevelopmentService: Using fallback data');
      return this.fallbackData;
    }
  }

  // Transform API data to match component expectations
  transformCoursesData(apiData) {
    const transformed = {
      totalCourse: apiData.totalCourses || this.fallbackData.totalCourses,
      registeredTrainee: apiData.totalRegister || this.fallbackData.totalRegister,
      achievedCertification: apiData.totalcomplete || this.fallbackData.totalcomplete,
      trainingNumber: apiData.trainingNumber || this.fallbackData.trainingNumber,
      hasUpcomingBatches: apiData.hasUpcomingBatches || this.fallbackData.hasUpcomingBatches,
    };
    
    return transformed;
  }

  // Fetch training categories through backend CMS
  async getTrainingCategories() {
    try {
      const response = await fetch(`${this.cmsBaseURL}/external-data/ebs-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: '/request/get_training_categories',
          data: {} // Add empty data object to match API expectation
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.data && result.data.status === 200 && result.data.data && result.data.data.categories) {
        return result.data.data.categories; // Return the categories array
      } else {
        console.warn('No categories data received');
        return this.getFallbackCategoriesData();
      }
    } catch (error) {
      console.warn('Error fetching training categories:', error);
      return this.getFallbackCategoriesData();
    }
  }

  // Fetch category-wise courses through backend CMS
  async getCategorywiseCourses(categoryId) {
    try {
      const response = await fetch(`${this.cmsBaseURL}/external-data/ebs-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: `/request/get_categorywise_courses?id=${categoryId}`,
          data: {}
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      // Check if we have valid data
      if (result.data && result.data.status === 200 && result.data.data) {
        return result.data.data; // Return the courses array
      } else if (result.data && typeof result.data === 'string' && result.data.includes('Invalid Id')) {
        console.warn('EBS API returned invalid ID error for category:', categoryId);
        console.warn('Falling back to upcoming courses data as temporary solution');
        
        // Temporary workaround: get upcoming courses as fallback
        try {
          const upcomingCourses = await this.getUpcomingCourses();
          return {
            courses: upcomingCourses || [],
            message: 'Showing upcoming courses as categorywise courses endpoint is not fully available',
            categoryId: categoryId,
            status: 'fallback_to_upcoming'
          };
        } catch (fallbackError) {
          console.warn('Fallback to upcoming courses also failed:', fallbackError);
          return this.getFallbackCoursesData();
        }
      } else {
        console.warn('No category-wise courses data received');
        return this.getFallbackCoursesData();
      }
    } catch (error) {
      console.warn('Error fetching category-wise courses:', error);
      return this.getFallbackCoursesData();
    }
  }

  // Fetch upcoming courses through backend CMS
  async getUpcomingCourses() {
    try {
      const response = await fetch(`${this.cmsBaseURL}/external-data/ebs-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: '/request/get_upcoming_courses',
          data: {} // Add empty data object to match API expectation
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.data && result.data.status === 200 && result.data.data) {
        return result.data.data; // Return the courses array
      } else {
        console.warn('No upcoming courses data received');
        return this.getFallbackCoursesData();
      }
    } catch (error) {
      console.warn('Error fetching upcoming courses:', error);
      return this.getFallbackCoursesData();
    }
  }

  // Fetch course details by ID through backend CMS
  async getCourseDetails(courseId) {
    try {
      if (!courseId) {
        console.warn('No course ID provided for course details');
        return null;
      }

      // Use the provided courseId as training_id for the API call (no fallback needed)
      const trainingId = courseId;
      const response = await fetch(`${this.cmsBaseURL}/external-data/ebs-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: `/request/get_course_details?id=${trainingId}`,
          data: {
            training_id: trainingId
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.warn(`HTTP error! status: ${response.status}`, errorText);
        return null;
      }

      const result = await response.json();
      // Handle the CMS wrapper response structure
      if (result.data && result.data.status === 200 && result.data.response === 'success' && result.data.data) {
        return result.data.data;
      } else {
        console.warn(`API response error: ${result.data?.msg || 'Unknown error'}`);
        console.warn('Response data:', result);
        return null;
      }
    } catch (error) {
      console.warn('Error fetching course details:', error);
      return null;
    }
  }

  // Helper method to process image URLs
  processImageUrl(imageUrl) {
    if (!imageUrl) return null;
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // If it's a relative URL, you might want to prepend a base URL
    // This depends on how your EBS API serves images
    return imageUrl;
  }

  // Helper method to get fallback courses data
  getFallbackCoursesData() {
    return [
      {
        id: 1,
        title: "Web Development Fundamentals",
        description: "Learn the basics of web development including HTML, CSS, and JavaScript",
        duration: "3 months",
        level: "Beginner",
        category: "Web Development"
      },
      {
        id: 2,
        title: "Advanced JavaScript",
        description: "Master advanced JavaScript concepts and modern frameworks",
        duration: "2 months",
        level: "Advanced",
        category: "Programming"
      }
    ];
  }

  // Helper method to get fallback categories data
  getFallbackCategoriesData() {
    return [
      { id: 1, name: "Web Development", course_count: 15 },
      { id: 2, name: "Programming", course_count: 12 },
      { id: 3, name: "Database", course_count: 8 },
      { id: 4, name: "Mobile App Development", course_count: 10 }
    ];
  }

  // Check if token is valid and not expired (legacy method for compatibility)
  isTokenValid() {
    // This method is kept for backward compatibility
    // but token management is now handled by backend
    return true;
  }

  // Get valid token (legacy method for compatibility)
  async getValidToken() {
    // This method is kept for backward compatibility
    // but token management is now handled by backend
    return 'backend-managed-token';
  }
}

// Create and export singleton instance
const skillDevelopmentService = new SkillDevelopmentService();
export default skillDevelopmentService;